import type { PropertyDetail, PropertyOwnerPerson, PropertySummary } from "@/types/entities";
import { readPropertyBranchId } from "@/lib/propertyBranch";

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function joinParts(...parts: (string | null | undefined)[]): string | null {
  const items = parts.map((p) => str(p)).filter((p): p is string => Boolean(p));
  return items.length ? items.join(", ") : null;
}

type AddressDetailsLike = {
  displayAddress?: string | null;
  addressSubDwelling?: string | null;
  addressNameNumber?: string | null;
  street?: string | null;
  locality?: string | null;
  town?: string | null;
  county?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  addressLine4?: string | null;
  postcode?: string | null;
  buildingName?: string | null;
  buildingNumber?: string | null;
};

function readListAddressFields(row: PropertySummary & Record<string, unknown>) {
  const nested = row.addressDetails as AddressDetailsLike | null | undefined;

  const addressSubDwelling =
    str(row.addressSubDwelling) ?? str(nested?.addressSubDwelling);

  const nameNumber =
    str(row.addressNameNumber) ??
    str(nested?.addressNameNumber) ??
    str(row.buildingNumber) ??
    str(nested?.buildingNumber) ??
    str(row.buildingName) ??
    str(nested?.buildingName);

  const displayAddress = str(row.displayAddress) ?? str(nested?.displayAddress);

  return { addressSubDwelling, nameNumber, displayAddress };
}

/**
 * Property column label for list rows.
 * With sub dwelling: "Sub Dwelling, Name/Number, displayAddress".
 * Without sub dwelling: "Name/Number, displayAddress".
 */
export function propertyListDisplayAddress(
  row: PropertySummary | (PropertySummary & Record<string, unknown>),
  fallback = "(no address)",
): string {
  const { addressSubDwelling, nameNumber, displayAddress } = readListAddressFields(
    row as PropertySummary & Record<string, unknown>,
  );

  if (addressSubDwelling) {
    const label = joinParts(addressSubDwelling, nameNumber, displayAddress);
    if (label) return label;
  } else {
    const label = joinParts(nameNumber, displayAddress);
    if (label) return label;
  }

  if (displayAddress) return displayAddress;

  const ext = str(row.externalId);
  if (ext) return `Property ${ext}`;

  return fallback;
}

/** Resolve a human-readable property title from GET /properties/{id} or list stubs. */
export function propertyDisplayLabel(
  p: PropertySummary | PropertyDetail | (PropertySummary & Record<string, unknown>),
  fallback = "Unnamed Property",
): string {
  const raw = p as Record<string, unknown>;
  const direct = str(p.displayAddress) ?? str(raw.displayAddress);
  if (direct) return direct;

  const nested = raw.addressDetails as AddressDetailsLike | null | undefined;
  const nestedDisplay = str(nested?.displayAddress);
  if (nestedDisplay) return nestedDisplay;

  const fromLines = joinParts(
    str(nested?.addressSubDwelling) ?? str(raw.addressSubDwelling),
    str(nested?.addressNameNumber) ??
      str(raw.addressNameNumber) ??
      str(nested?.buildingNumber) ??
      str(raw.buildingNumber),
    str(nested?.street) ?? str(raw.street) ?? str(nested?.addressLine1) ?? str(raw.addressLine1),
    typeof raw.locality === "string"
      ? str(raw.locality)
      : str(nested?.addressLine2) ?? str(raw.addressLine2),
    str(nested?.town) ?? str(raw.town) ?? str(nested?.addressLine3) ?? str(raw.addressLine3),
    str(nested?.county) ?? str(raw.county) ?? str(nested?.addressLine4) ?? str(raw.addressLine4),
    str(nested?.postcode) ?? str(raw.postcode),
  );
  if (fromLines) return fromLines;

  const summary = str(raw.summary);
  if (summary) return summary.length > 120 ? `${summary.slice(0, 117)}…` : summary;

  const strapline = str(raw.strapline);
  if (strapline) return strapline;

  const listing = str(raw.listingAdvertisementNumber);
  if (listing) return `Listing ${listing}`;

  const ext = str(p.externalId) ?? str(raw.externalId);
  if (ext) return `Property ${ext}`;

  return fallback;
}

/** Human-readable label for a property owner/vendor person stub. */
export function formatPropertyOwnerLabel(owner: PropertyOwnerPerson): string {
  const parts = [owner.title, owner.forename, owner.surname].filter(Boolean).join(" ").trim();
  return parts || owner.externalContactId || owner.contactId || "";
}

/** Resolve vendor name from list row fields or embedded `owners`. */
export function propertyVendorNameFromRow(
  row: PropertySummary & { owners?: PropertyOwnerPerson[] | null },
): string | null {
  const explicit = typeof row.vendorName === "string" ? row.vendorName.trim() : "";
  if (explicit) return explicit;
  const first = row.owners?.[0];
  if (!first) return null;
  const label = formatPropertyOwnerLabel(first);
  return label || null;
}

/** Normalize a `GET /properties` list item for the properties table. */
export function normalizePropertyListRow(raw: PropertySummary): PropertySummary {
  const row = raw as PropertySummary & { owners?: PropertyOwnerPerson[] | null };
  const vendorName = propertyVendorNameFromRow(row);
  const branchId = readPropertyBranchId(row);
  const legalEntityId =
    typeof (row as PropertySummary & { legalEntityId?: unknown }).legalEntityId === "string"
      ? (row as PropertySummary & { legalEntityId?: string }).legalEntityId!.trim() || null
      : null;
  return {
    ...raw,
    sourceCrm: raw.sourceCrm ?? null,
    externalId: raw.externalId ?? null,
    displayAddress: propertyListDisplayAddress(row),
    market: raw.market ?? null,
    atlasModifiedAt: raw.atlasModifiedAt ?? null,
    ...(branchId ? { branchId } : {}),
    ...(legalEntityId ? { legalEntityId } : {}),
    ...(vendorName ? { vendorName } : {}),
  };
}

/** Subtitle under address on the properties list (market, CRM, external id). */
export function propertyListSubtitle(row: PropertySummary): string {
  const parts = [row.market]
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

/** Unwrap axios `{ data }` or return the payload as-is. */
export function unwrapApiData<T>(res: unknown): T {
  if (res && typeof res === "object" && "data" in res) {
    return (res as { data: T }).data;
  }
  return res as T;
}

/** Map a create/get property response into a list-row {@link PropertySummary}. */
export function propertyResponseToSummary(raw: unknown): PropertySummary | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id);
  if (!id) return null;

  const stub = { ...r, id } as PropertySummary;
  const base: PropertySummary = {
    id,
    sourceCrm: str(r.sourceCrm),
    externalId: str(r.externalId),
    displayAddress: propertyDisplayLabel(stub),
    propertyType: str(r.propertyType),
    availabilityStatus: str(r.availabilityStatus),
    availabilityColor: (str(r.availabilityColor)?.toUpperCase() as PropertySummary["availabilityColor"]) ?? null,
    price: typeof r.price === "number" ? r.price : null,
    currency: str(r.currency),
    bedrooms: typeof r.bedrooms === "number" ? r.bedrooms : null,
    bathrooms: typeof r.bathrooms === "number" ? r.bathrooms : null,
    externalBranchId: str(r.externalBranchId),
    externalUserId: str(r.externalUserId),
    market: str(r.market),
    atlasModifiedAt: str(r.atlasModifiedAt) ?? str(r.modifiedDate) ?? new Date().toISOString(),
  };
  const owners = Array.isArray(r.owners) ? (r.owners as PropertyOwnerPerson[]) : undefined;
  return normalizePropertyListRow({ ...base, ...(owners ? { owners } : {}) } as PropertySummary & {
    owners?: PropertyOwnerPerson[];
  });
}

/** Ensure {@link PropertyDetail.displayAddress} is set after GET /properties/{id}. */
export function normalizePropertyDetail(raw: PropertyDetail): PropertyDetail {
  const label = propertyDisplayLabel(raw);
  if (label === "Unnamed Property") return raw;
  return {
    ...raw,
    displayAddress: str(raw.displayAddress) ?? label,
  };
}
